import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { AuthLayout } from "@/components/auth-layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { APP_NAME } from "@/lib/app-info";
import { logger } from "@/lib/logger";

import { MICROSOFT_SIGN_IN_PATH, microsoftSignInError } from "./utils";

/** The sign-in screen: Microsoft is the only way in. */
export function LoginPage() {
  const { t } = useTranslation();
  // A failed Microsoft sign-in comes back as /login?error=<code>.
  const [searchParams] = useSearchParams();
  const errorCode = searchParams.get("error");
  const microsoftError = microsoftSignInError(errorCode);

  useEffect(() => {
    if (errorCode) {
      logger.debug("[login] Microsoft sign-in came back with", errorCode);
    }
  }, [errorCode]);

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        {/* The brand panel's logo names the place, so the card needs no title of its own. */}
        <h1 className="sr-only">{t("login.title", { app: APP_NAME })}</h1>
        <CardContent className="space-y-3 pt-6">
          {/* A full-page navigation: the sign-in is a chain of redirects, not an API call. */}
          <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 w-full">
            <a
              href={MICROSOFT_SIGN_IN_PATH}
              onClick={() => logger.debug("[login] starting Microsoft sign-in")}
            >
              <span
                aria-hidden="true"
                className="bg-card flex h-5 w-5 items-center justify-center rounded-sm"
              >
                <MicrosoftLogo className="h-3.5 w-3.5" />
              </span>
              {t("login.microsoft")}
            </a>
          </Button>
          <p className="text-muted text-center text-sm">{t("login.hint")}</p>
          {microsoftError && (
            <p role="alert" className="text-destructive text-sm">
              {microsoftError}
            </p>
          )}
        </CardContent>
      </Card>
    </AuthLayout>
  );
}

/**
 * Microsoft's four-square logo. Its colours are fixed by Microsoft's brand
 * rules for sign-in buttons, so they live only here, the way Velta's violet
 * lives only in its logo file; they are no part of the De Ceuster palette.
 */
function MicrosoftLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 21 21" aria-hidden="true" focusable="false" className={className}>
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}
